import { WAREHOUSE_FILTER_QUERY_NAME } from 'types/global';
import { IbackofficeUserData } from 'types/configuration.types';
import { SLA_STATUS } from 'types/orders';
import { nudosRed100, nudosYellow200, nudosGreen300 } from 'styles/styles.constants';

export const FILTER_NAMES = {
  COMPANY: 'company',
  MONTH: 'month',
  PAYMENT_TYPE: 'paymentType',
  COUNTRY_DESTINATION: 'countryDestination',
  HAS_INVOICE: 'hasInvoice',
  KAM: 'kam',
  RESPONSIBLE: 'responsible',
  MISSING: 'missing',
  SLA_ETA: 'slaEta',
  CONFIGURATION: 'configuration',
  PREPARATION: 'preparation',
  OTHERS: 'others',
  ORDER_TYPE: 'orderType',
  SORT: 'sort',
  QUOTATION: 'quotation',
  WAREHOUSE_FILTER: WAREHOUSE_FILTER_QUERY_NAME
} as const;

export const FILTER_TITLES = {
  COMPANY: 'Empresa',
  MONTH: 'Mes',
  PAYMENT_METHOD: 'Método pago',
  COUNTRY: 'País',
  INVOICE: 'Factura',
  KAM: 'KAM',
  RESPONSIBLE: 'Responsable',
  MISSING: 'Faltantes',
  SLA_ETA: 'SLA y ETA',
  CONFIGURATION: 'Configuración',
  PREPARATION: 'Preparación',
  OTHERS: 'Otros',
  ORDER_TYPE: 'Tipo de orden',
  SORT: 'Ordenar',
  WAREHOUSE_FILTER: 'Gestión de bodega'
} as const;

export const SEARCH_PLACEHOLDERS = {
  COMPANY: 'Buscar empresa',
  COUNTRY: 'Buscar país',
  KAM: 'Buscar KAM',
  RESPONSIBLE: 'Buscar responsable',
  MONTH: 'Buscar mes',
  PAYMENT_METHOD: 'Buscar método de pago'
} as const;

export const SORT_FILTER_CONTAINER_TITLE = 'Ordenar por:';

export const ORDERS_FILTER_MAPPING: Record<string, string> = {
  [FILTER_TITLES.COMPANY]: FILTER_NAMES.COMPANY,
  [FILTER_TITLES.MONTH]: FILTER_NAMES.MONTH,
  [FILTER_TITLES.PAYMENT_METHOD]: FILTER_NAMES.PAYMENT_TYPE,
  [FILTER_TITLES.COUNTRY]: FILTER_NAMES.COUNTRY_DESTINATION,
  [FILTER_TITLES.INVOICE]: FILTER_NAMES.HAS_INVOICE,
  [FILTER_TITLES.KAM]: FILTER_NAMES.KAM,
  [FILTER_TITLES.RESPONSIBLE]: FILTER_NAMES.RESPONSIBLE,
  [FILTER_TITLES.MISSING]: FILTER_NAMES.MISSING,
  [FILTER_TITLES.SLA_ETA]: FILTER_NAMES.SLA_ETA,
  [FILTER_TITLES.CONFIGURATION]: FILTER_NAMES.CONFIGURATION,
  [FILTER_TITLES.PREPARATION]: FILTER_NAMES.PREPARATION,
  [FILTER_TITLES.OTHERS]: FILTER_NAMES.OTHERS,
  [FILTER_TITLES.ORDER_TYPE]: FILTER_NAMES.ORDER_TYPE,
  [FILTER_TITLES.SORT]: FILTER_NAMES.SORT
} as const;

export interface IOrderFilterListStaticConfig {
  filterName: string;
  filterTitle: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  sortAlphabetically?: boolean;
  titleFilterContainer?: string;
  useRadioButtons?: boolean;
  useMultiSelect?: boolean;
}

export const ORDER_FILTERS_LIST_CONFIG: IOrderFilterListStaticConfig[] = [
  {
    filterName: FILTER_NAMES.COMPANY,
    filterTitle: FILTER_TITLES.COMPANY,
    showSearch: true,
    searchPlaceholder: SEARCH_PLACEHOLDERS.COMPANY,
    useMultiSelect: true
  },
  {
    filterName: FILTER_NAMES.MONTH,
    filterTitle: FILTER_TITLES.MONTH,
    showSearch: true,
    searchPlaceholder: SEARCH_PLACEHOLDERS.MONTH,
    useMultiSelect: true
  },
  {
    filterName: FILTER_NAMES.COUNTRY_DESTINATION,
    filterTitle: FILTER_TITLES.COUNTRY,
    showSearch: true,
    searchPlaceholder: SEARCH_PLACEHOLDERS.COUNTRY,
    sortAlphabetically: true,
    useMultiSelect: true
  },
  {
    filterName: FILTER_NAMES.PAYMENT_TYPE,
    filterTitle: FILTER_TITLES.PAYMENT_METHOD,
    showSearch: true,
    searchPlaceholder: SEARCH_PLACEHOLDERS.PAYMENT_METHOD,
    useMultiSelect: true
  },
  { filterName: FILTER_NAMES.HAS_INVOICE, filterTitle: FILTER_TITLES.INVOICE },
  {
    filterName: FILTER_NAMES.KAM,
    filterTitle: FILTER_TITLES.KAM,
    showSearch: true,
    searchPlaceholder: SEARCH_PLACEHOLDERS.KAM,
    useMultiSelect: true
  },
  {
    filterName: FILTER_NAMES.RESPONSIBLE,
    filterTitle: FILTER_TITLES.RESPONSIBLE,
    showSearch: true,
    searchPlaceholder: SEARCH_PLACEHOLDERS.RESPONSIBLE
  },
  { filterName: FILTER_NAMES.MISSING, filterTitle: FILTER_TITLES.MISSING },
  { filterName: FILTER_NAMES.SLA_ETA, filterTitle: FILTER_TITLES.SLA_ETA },
  { filterName: FILTER_NAMES.CONFIGURATION, filterTitle: FILTER_TITLES.CONFIGURATION, useMultiSelect: true },
  { filterName: FILTER_NAMES.ORDER_TYPE, filterTitle: FILTER_TITLES.ORDER_TYPE, useMultiSelect: true },
  { filterName: FILTER_NAMES.PREPARATION, filterTitle: FILTER_TITLES.PREPARATION },
  { filterName: FILTER_NAMES.OTHERS, filterTitle: FILTER_TITLES.OTHERS, useMultiSelect: true },
  {
    filterName: FILTER_NAMES.SORT,
    filterTitle: FILTER_TITLES.SORT,
    titleFilterContainer: SORT_FILTER_CONTAINER_TITLE,
    useRadioButtons: true
  }
];

const MISSING_OPTION_LABELS = {
  WITHOUT_SERIAL: 'Sin serial',
  WITHOUT_COST: 'Sin costo',
  WITHOUT_PROVIDER: 'Sin Proveedor',
  WITHOUT_PO: 'Sin PO proveedor',
  WITHOUT_MPN: 'Sin MPN'
} as const;

export const MISSING_OPTION_VALUES = {
  WITHOUT_SERIAL: 'serial',
  WITHOUT_COST: 'purchaseValue',
  WITHOUT_PROVIDER: 'providerName',
  WITHOUT_PO: 'providerPurchaseOrder',
  WITHOUT_MPN: 'mpnId'
} as const;

export const MISSING_FILTER_OPTIONS: string[] = [
  MISSING_OPTION_LABELS.WITHOUT_SERIAL,
  MISSING_OPTION_LABELS.WITHOUT_COST,
  MISSING_OPTION_LABELS.WITHOUT_PROVIDER,
  MISSING_OPTION_LABELS.WITHOUT_PO,
  MISSING_OPTION_LABELS.WITHOUT_MPN
];

export const MISSING_NAME_TO_VALUE_MAP = new Map<string, string>([
  [MISSING_OPTION_LABELS.WITHOUT_SERIAL, MISSING_OPTION_VALUES.WITHOUT_SERIAL],
  [MISSING_OPTION_LABELS.WITHOUT_COST, MISSING_OPTION_VALUES.WITHOUT_COST],
  [MISSING_OPTION_LABELS.WITHOUT_PROVIDER, MISSING_OPTION_VALUES.WITHOUT_PROVIDER],
  [MISSING_OPTION_LABELS.WITHOUT_PO, MISSING_OPTION_VALUES.WITHOUT_PO],
  [MISSING_OPTION_LABELS.WITHOUT_MPN, MISSING_OPTION_VALUES.WITHOUT_MPN]
]);

export const MISSING_VALUE_TO_NAME_MAP = new Map<string, string>([
  [MISSING_OPTION_VALUES.WITHOUT_SERIAL, MISSING_OPTION_LABELS.WITHOUT_SERIAL],
  [MISSING_OPTION_VALUES.WITHOUT_COST, MISSING_OPTION_LABELS.WITHOUT_COST],
  [MISSING_OPTION_VALUES.WITHOUT_PROVIDER, MISSING_OPTION_LABELS.WITHOUT_PROVIDER],
  [MISSING_OPTION_VALUES.WITHOUT_PO, MISSING_OPTION_LABELS.WITHOUT_PO],
  [MISSING_OPTION_VALUES.WITHOUT_MPN, MISSING_OPTION_LABELS.WITHOUT_MPN]
]);

const CONFIGURATION_OPTION_LABELS = {
  COMPLETED: 'Configuraciones completadas',
  PENDING: 'Configuraciones pendientes',
  PENDING_APPROVAL: 'Configuraciones por aprobar'
} as const;

const CONFIGURATION_OPTION_VALUES = {
  COMPLETED: 'Completado',
  PENDING: 'Por Completar',
  PENDING_APPROVAL: 'Por Aprobar'
} as const;

export const CONFIGURATION_FILTER_OPTIONS: string[] = [
  CONFIGURATION_OPTION_LABELS.COMPLETED,
  CONFIGURATION_OPTION_LABELS.PENDING,
  CONFIGURATION_OPTION_LABELS.PENDING_APPROVAL
];

export const CONFIGURATION_NAME_TO_VALUE_MAP = new Map<string, string>([
  [CONFIGURATION_OPTION_LABELS.COMPLETED, CONFIGURATION_OPTION_VALUES.COMPLETED],
  [CONFIGURATION_OPTION_LABELS.PENDING, CONFIGURATION_OPTION_VALUES.PENDING],
  [CONFIGURATION_OPTION_LABELS.PENDING_APPROVAL, CONFIGURATION_OPTION_VALUES.PENDING_APPROVAL]
]);

export const CONFIGURATION_VALUE_TO_NAME_MAP = new Map<string, string>([
  [CONFIGURATION_OPTION_VALUES.COMPLETED, CONFIGURATION_OPTION_LABELS.COMPLETED],
  [CONFIGURATION_OPTION_VALUES.PENDING, CONFIGURATION_OPTION_LABELS.PENDING],
  [CONFIGURATION_OPTION_VALUES.PENDING_APPROVAL, CONFIGURATION_OPTION_LABELS.PENDING_APPROVAL]
]);

const INVOICE_OPTION_LABELS = {
  LOADED: 'Cargada',
  WITHOUT_INVOICE: 'Sin factura'
} as const;

const INVOICE_OPTION_VALUES = {
  LOADED: 'true',
  WITHOUT_INVOICE: 'false'
} as const;

export const INVOICE_NAME_TO_BOOLEAN_MAP = new Map<string, string>([
  [INVOICE_OPTION_LABELS.LOADED, INVOICE_OPTION_VALUES.LOADED],
  [INVOICE_OPTION_LABELS.WITHOUT_INVOICE, INVOICE_OPTION_VALUES.WITHOUT_INVOICE]
]);

export const INVOICE_FILTER_OPTIONS: string[] = [INVOICE_OPTION_LABELS.LOADED, INVOICE_OPTION_LABELS.WITHOUT_INVOICE];

const PREPARATION_OPTION_LABELS = {
  VALIDATED: 'Información validada',
  PURCHASED_FROM_PROVIDER: 'Comprado al proveedor',
  PURCHASE_ORDER_LOADED: 'Orden de compra cargada',
  PAYMENT_PROOF_SENT: 'Comprobante de pago enviado'
} as const;

const PREPARATION_OPTION_VALUES = {
  VALIDATED: 'informationValidated',
  PURCHASED_FROM_PROVIDER: 'purchasedFromProvider',
  PURCHASE_ORDER_LOADED: 'purchaseOrderLoaded',
  PAYMENT_PROOF_SENT: 'paymentProofSent'
} as const;

export const PREPARATION_OPTIONS_OBJECT = {
  [PREPARATION_OPTION_LABELS.VALIDATED]: PREPARATION_OPTION_VALUES.VALIDATED,
  [PREPARATION_OPTION_LABELS.PURCHASED_FROM_PROVIDER]: PREPARATION_OPTION_VALUES.PURCHASED_FROM_PROVIDER,
  [PREPARATION_OPTION_LABELS.PURCHASE_ORDER_LOADED]: PREPARATION_OPTION_VALUES.PURCHASE_ORDER_LOADED,
  [PREPARATION_OPTION_LABELS.PAYMENT_PROOF_SENT]: PREPARATION_OPTION_VALUES.PAYMENT_PROOF_SENT
} as const;

export const PREPARATION_OPTIONS_LIST = new Map(Object.entries(PREPARATION_OPTIONS_OBJECT));

export const PREPARATION_VALUE_TO_FIELD_ID_MAP = new Map<string, number>([
  [PREPARATION_OPTION_VALUES.VALIDATED, 1],
  [PREPARATION_OPTION_VALUES.PURCHASED_FROM_PROVIDER, 2],
  [PREPARATION_OPTION_VALUES.PURCHASE_ORDER_LOADED, 3],
  [PREPARATION_OPTION_VALUES.PAYMENT_PROOF_SENT, 4]
]);

const OTHERS_OPTION_LABELS = {
  NEW_ENTRY: 'Nuevo ingreso',
  WAREHOUSE_STOP: 'Parada en bodega',
  NO_WAREHOUSE_STOP: 'Sin parada en bodega',
  QUOTATION: 'Cotización'
} as const;

export const OTHERS_OPTION_VALUES = {
  NEW_ENTRY: 'newEntry',
  WAREHOUSE_STOP: 'warehouseStop',
  NO_WAREHOUSE_STOP: 'noWarehouseStop',
  QUOTATION: 'withQuote'
} as const;

export const OTHERS_NAME_TO_VALUE_MAP = new Map<string, string>([
  [OTHERS_OPTION_LABELS.NEW_ENTRY, OTHERS_OPTION_VALUES.NEW_ENTRY],
  [OTHERS_OPTION_LABELS.WAREHOUSE_STOP, OTHERS_OPTION_VALUES.WAREHOUSE_STOP],
  [OTHERS_OPTION_LABELS.NO_WAREHOUSE_STOP, OTHERS_OPTION_VALUES.NO_WAREHOUSE_STOP],
  [OTHERS_OPTION_LABELS.QUOTATION, OTHERS_OPTION_VALUES.QUOTATION]
]);

export const OTHERS_FILTER_OPTIONS: string[] = [
  OTHERS_OPTION_LABELS.NEW_ENTRY,
  OTHERS_OPTION_LABELS.WAREHOUSE_STOP,
  OTHERS_OPTION_LABELS.NO_WAREHOUSE_STOP,
  OTHERS_OPTION_LABELS.QUOTATION
];

/**
 * Resuelve la selección del filtro Otros aplicando exclusión mutua entre parada y sin parada en bodega.
 * @param selectedValue - Valor interno de la opción seleccionada
 * @param currentValues - Valores actualmente seleccionados
 * @param isAdding - Si la opción se está agregando (true) o removiendo (false)
 * @returns Nuevo arreglo de valores seleccionados
 */
export const resolveOthersFilterSelection = (
  selectedValue: string,
  currentValues: string[],
  isAdding: boolean
): string[] => {
  if (!isAdding) {
    return currentValues.filter(value => value !== selectedValue);
  }

  const withoutMutexPeer = currentValues.filter(value => {
    if (selectedValue === OTHERS_OPTION_VALUES.WAREHOUSE_STOP) {
      return value !== OTHERS_OPTION_VALUES.NO_WAREHOUSE_STOP;
    }
    if (selectedValue === OTHERS_OPTION_VALUES.NO_WAREHOUSE_STOP) {
      return value !== OTHERS_OPTION_VALUES.WAREHOUSE_STOP;
    }
    return true;
  });

  return [...withoutMutexPeer, selectedValue];
};

const ORDER_TYPE_OPTION_LABELS = {
  BUY_AND_HOLD: 'Buy & Hold',
  TO_EMPLOYEE: 'A empleado',
  TO_OFFICE: 'A oficina'
} as const;

export const ORDER_TYPE_OPTION_VALUES = {
  BUY_AND_HOLD: 'buyAndHold',
  TO_EMPLOYEE: 'employee',
  TO_OFFICE: 'office'
} as const;

export const ORDER_TYPE_FILTER_OPTIONS: string[] = [
  ORDER_TYPE_OPTION_LABELS.BUY_AND_HOLD,
  ORDER_TYPE_OPTION_LABELS.TO_EMPLOYEE,
  ORDER_TYPE_OPTION_LABELS.TO_OFFICE
];

export const ORDER_TYPE_NAME_TO_VALUE_MAP = new Map<string, string>([
  [ORDER_TYPE_OPTION_LABELS.BUY_AND_HOLD, ORDER_TYPE_OPTION_VALUES.BUY_AND_HOLD],
  [ORDER_TYPE_OPTION_LABELS.TO_EMPLOYEE, ORDER_TYPE_OPTION_VALUES.TO_EMPLOYEE],
  [ORDER_TYPE_OPTION_LABELS.TO_OFFICE, ORDER_TYPE_OPTION_VALUES.TO_OFFICE]
]);

export const ETA_OVERDUE_VALUE = 'etaOverdue';

const SLA_ETA_OPTION_LABELS = {
  ETA_OVERDUE: 'ETA Atrasado',
  SLA_OVERDUE: 'SLA Atrasado',
  WARNING_24H: '1 día antes del SLA',
  WARNING_48H: '2 días antes del SLA',
  ON_TIME: 'A tiempo'
} as const;

export const SLA_ETA_FILTER_OPTIONS: string[] = [
  SLA_ETA_OPTION_LABELS.ETA_OVERDUE,
  SLA_ETA_OPTION_LABELS.SLA_OVERDUE,
  SLA_ETA_OPTION_LABELS.WARNING_24H,
  SLA_ETA_OPTION_LABELS.WARNING_48H,
  SLA_ETA_OPTION_LABELS.ON_TIME
];

export const SLA_ETA_NAME_TO_VALUE_MAP = new Map<string, string>([
  [SLA_ETA_OPTION_LABELS.ETA_OVERDUE, ETA_OVERDUE_VALUE],
  [SLA_ETA_OPTION_LABELS.SLA_OVERDUE, SLA_STATUS.OVERDUE],
  [SLA_ETA_OPTION_LABELS.WARNING_24H, SLA_STATUS.WARNING_24H],
  [SLA_ETA_OPTION_LABELS.WARNING_48H, SLA_STATUS.WARNING_48H],
  [SLA_ETA_OPTION_LABELS.ON_TIME, SLA_STATUS.ON_TIME]
]);

export const SLA_ETA_VALUE_TO_NAME_MAP = new Map<string, string>([
  [ETA_OVERDUE_VALUE, SLA_ETA_OPTION_LABELS.ETA_OVERDUE],
  [SLA_STATUS.OVERDUE, SLA_ETA_OPTION_LABELS.SLA_OVERDUE],
  [SLA_STATUS.WARNING_24H, SLA_ETA_OPTION_LABELS.WARNING_24H],
  [SLA_STATUS.WARNING_48H, SLA_ETA_OPTION_LABELS.WARNING_48H],
  [SLA_STATUS.ON_TIME, SLA_ETA_OPTION_LABELS.ON_TIME]
]);

export const SLA_ETA_OPTION_COLOR_MAP = new Map<string, string>([
  [SLA_ETA_OPTION_LABELS.ETA_OVERDUE, nudosRed100],
  [SLA_ETA_OPTION_LABELS.SLA_OVERDUE, nudosRed100],
  [SLA_ETA_OPTION_LABELS.WARNING_24H, nudosRed100],
  [SLA_ETA_OPTION_LABELS.WARNING_48H, nudosYellow200],
  [SLA_ETA_OPTION_LABELS.ON_TIME, nudosGreen300]
]);

export const SORT_OPTION_LABELS = {
  CONFIRMED_DATE: 'Fecha confirmado',
  ORDER_NUMBER: 'Número de la orden',
  ETA_DESTINATION: 'ETA Destino',
  UNIT_QUANTITY: 'Cantidad de unidad'
} as const;

export const SORT_OPTION_VALUES = {
  CONFIRMED_DATE: 'confirmedAt',
  ORDER_NUMBER: 'orderId',
  ETA_DESTINATION: 'estimatedDeliveryDate',
  UNIT_QUANTITY: 'numberOfProducts'
} as const;

export const SORT_OPTIONS: string[] = [
  SORT_OPTION_LABELS.CONFIRMED_DATE,
  SORT_OPTION_LABELS.ORDER_NUMBER,
  SORT_OPTION_LABELS.ETA_DESTINATION,
  SORT_OPTION_LABELS.UNIT_QUANTITY
];

export const SORT_OPTIONS_TO_VALUE_MAP = new Map<string, string>([
  [SORT_OPTION_LABELS.CONFIRMED_DATE, SORT_OPTION_VALUES.CONFIRMED_DATE],
  [SORT_OPTION_LABELS.ORDER_NUMBER, SORT_OPTION_VALUES.ORDER_NUMBER],
  [SORT_OPTION_LABELS.ETA_DESTINATION, SORT_OPTION_VALUES.ETA_DESTINATION],
  [SORT_OPTION_LABELS.UNIT_QUANTITY, SORT_OPTION_VALUES.UNIT_QUANTITY]
]);

export const SORT_DEFAULT_SORT_BY = SORT_OPTION_VALUES.ORDER_NUMBER;

export const noneResponsible = {
  email: 'Sin responsables asignados',
  id: 0,
  firstName: '',
  lastName: ''
} as IbackofficeUserData;

export const FILTER_NAME_TO_BACKEND_MAPPING: Record<string, { entity: string; field: string }> = {
  [FILTER_NAMES.COMPANY]: { entity: 'order', field: 'organizationId' },
  [FILTER_NAMES.PAYMENT_TYPE]: { entity: 'payment', field: 'name' },
  [FILTER_NAMES.COUNTRY_DESTINATION]: { entity: 'country', field: 'name' },
  [FILTER_NAMES.HAS_INVOICE]: { entity: 'invoice', field: 'invoiceUrl' },
  [FILTER_NAMES.KAM]: { entity: 'order', field: 'organizationId' },
  [FILTER_NAMES.RESPONSIBLE]: { entity: 'responsible', field: 'userId' },
  [FILTER_NAMES.PREPARATION]: { entity: 'checklist', field: 'preparationChecklistValue' },
  [FILTER_NAMES.CONFIGURATION]: { entity: 'productConfig', field: 'status' },
  [FILTER_NAMES.OTHERS]: { entity: 'shipmentGroup', field: 'startDay' },
  [FILTER_NAMES.SLA_ETA]: { entity: 'sla', field: 'status' },
  [FILTER_NAMES.QUOTATION]: { entity: 'sale', field: 'quoteItemId' }
} as const;

export type TRawFilterValue = string | number | boolean | string[] | number[];
export type TFocusedFilterValue = TRawFilterValue | null;
export type TSyncFilterValue = string | string[] | null;

export interface ISyncFilterEntry {
  filterName: string;
  focusedValue: TRawFilterValue | null;
  getFilterValue: (v: TRawFilterValue) => TSyncFilterValue;
}

export interface IFocusedFilterValuesMap {
  [filterName: string]: TFocusedFilterValue;
}
